import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosInstance } from 'axios';
import { useLocalStorage } from '../../hooks/useLocalStorage';

import { ManagementService } from '../../services/ManagementService';
import { ICompany } from '../../services/Interfaces/IManagemenntService';
import { useApi } from '../../services/hooks/apiHook';

import { DynamicTable, TableColumn } from '../../components/Table';

export enum DocumentStatus {
  Created = 'created',
  Approved = 'approved',
  Rejected = 'rejected'
}

const DashboardCompaniesScreen: React.FunctionComponent = () => {
  const [token] = useLocalStorage('access-token', null);
  const { t, i18n } = useTranslation();
  const api: AxiosInstance = useApi();

  const [companies, setCompanies] = useState<ICompany[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageResponse, setCurrentPageResponse] = useState<number>(1);
  const [responsePerPage, setResponsePerPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [toNextPage, setToNextPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sortByValue, setSortByValue] = useState<string>('');
  const [sortDirectionValue, setSortDirectionValue] = useState<string>('asc');

  const typeCompanyRequest = 'new_company';

  const columns = [
    { columnKey: 'id', header: t('labels.lblID') },
    { columnKey: 'ibc_primary', header: t('others.othCompany') },
    { columnKey: 'total_authorized_shares', header: t('others.othShareCapital') },
    { columnKey: 'directors_count', header: t('others.othDirectors') },
    { columnKey: 'shareholders_count', header: t('others.othShareholders') },
    { columnKey: 'expire_process', header: t('others.othExpireProcess') },
    { columnKey: 'status', header: t('labels.lblStatus') }
  ] as TableColumn<any>[];

  const fetchListCompanies = async (page: number, sortBy: string, sortDirection: string) => {
    try {
      setIsLoading(true);
      const { data } = await ManagementService.listCompanies(api, typeCompanyRequest, sortBy, sortDirection, page, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Language': i18n.language.toLowerCase() }
      });

      setCompanies(data.data);
      setResponsePerPage(data.per_page);
      setCurrentPageResponse(data.current_page);
      setCurrentPage(page);
      setTotalItems(data.total);
      setToNextPage(data.last_page);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetch service list companies', error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchListCompanies(currentPage, sortByValue, sortDirectionValue);
  }, []);

  return (
    <>
      {isLoading ? (
        <div className="flex flex-row items-center justify-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-black"></div>
          <span>{t('OnboardingRequestLoadingData')}</span>
        </div>
      ) : (
        <>
          <div className="flex w-full flex-row justify-between border-b-[2px] pb-3">
            <div className="flex flex-col">
              <h2 className="text-xl font-bold text-[#0C1FAA]">{t('others.otheNewCompany')}</h2>
              <span className="font-light text-[#727272]">{t('others.otheNewCompany')}</span>
            </div>
          </div>

          <DynamicTable
            data={companies}
            columns={columns}
            itemsPerPage={responsePerPage}
            currentPage={currentPageResponse}
            totalItems={totalItems}
            toNextPage={toNextPage}
            onPageChange={(page, sortBy, sortDirection) => fetchListCompanies(page, sortBy, sortDirection)}
            sortBy={sortByValue}
            sortDirection={sortDirectionValue}
          />
        </>
      )}
    </>
  );
};

export { DashboardCompaniesScreen };
